import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

import useTokens from '@/hooks/use-tokens'
import type { APITokenInfo } from '@/utils/apis/helper'

const PAIR_SEPARATOR = '_'

const useTokenMintAddresses = (initMintAddresses: [string, string]) => {
  const router = useRouter()
  const { isLoading, tokenList } = useTokens()

  const [defaultMintAddresses, setDefaultMintAddresses] = useState(initMintAddresses)
  useEffect(() => {
    setDefaultMintAddresses((defaultMintAddresses) =>
      isEqual(defaultMintAddresses, initMintAddresses) ? defaultMintAddresses : initMintAddresses,
    )
  }, [initMintAddresses])

  const [mintAddresses, setMintAddresses] = useState<[string, string]>(['', ''])

  useEffect(() => {
    if (isLoading || !tokenList) return

    const queryPair = String(window.location.pathname.split('/').pop()).toLowerCase()
    const pairs = queryPair.split(PAIR_SEPARATOR)

    if (pairs.length < 1 || pairs.length > 2) return

    let foundTokenIn: APITokenInfo | undefined
    let foundTokenOut: APITokenInfo | undefined

    if (pairs.length === 1) {
      // if only one token in the query string, treat it as tokenOut
      foundTokenOut = tokenList.find((t) => t.token_symbol.toLowerCase() === pairs[0])

      if (!foundTokenOut) return
    } else {
      foundTokenIn = tokenList.find((t) => t.token_symbol.toLowerCase() === pairs[0])
      foundTokenOut = tokenList.find((t) => t.token_symbol.toLowerCase() === pairs[1])

      if (!foundTokenIn || !foundTokenOut) return
    }

    setMintAddresses((mintAddresses) => {
      const [prevInAddr, prevOutAddr] = mintAddresses
      const newDefaultInAddr = foundTokenIn?.coin_address || prevInAddr
      const newDefaultOutAddr = foundTokenOut?.coin_address || prevOutAddr

      const newAddr: [string, string] = newDefaultInAddr === newDefaultOutAddr
        ? ['', newDefaultOutAddr]
        : [newDefaultInAddr, newDefaultOutAddr]
      return isEqual(newAddr, mintAddresses) ? mintAddresses : newAddr
    })
  }, [isLoading, tokenList])

  useEffect(() => {
    if (!['/swap', '/swap/[pair]', '/limit/[pair]'].includes(router.pathname)) return
    if (isLoading || !tokenList) return

    const tokenIn = tokenList.find((t) => t.coin_address === mintAddresses[0])
    const tokenOut = tokenList.find((t) => t.coin_address === mintAddresses[1])

    if (tokenIn && !tokenOut) return

    const newPairQuery = [tokenIn?.token_symbol, tokenOut?.token_symbol]
      .filter(Boolean)
      .join(PAIR_SEPARATOR)

    if (!newPairQuery) return

    const searchParams = window.location.search
    const pathname = router.pathname === '/swap' ? '/swap/[pair]' : router.pathname

    window.history.replaceState({}, '', `${pathname.replace('[pair]', newPairQuery)}${searchParams}`)
  }, [isLoading, mintAddresses, router, tokenList])

  const finalMintAddresses = useMemo<[string, string]>(
    () => mintAddresses[0] || mintAddresses[1] ? mintAddresses : defaultMintAddresses,
    [defaultMintAddresses, mintAddresses],
  )

  const handleSetMintAddresses = useCallback<Dispatch<SetStateAction<[string, string]>>>(
    (updater) => {
      setMintAddresses(typeof updater === 'function' ? updater(finalMintAddresses) : updater)
    },
    [finalMintAddresses],
  )

  return {
    mintAddresses: finalMintAddresses,
    setMintAddresses: handleSetMintAddresses,
  }
}

export default useTokenMintAddresses
